import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, MoreVertical, ChevronDown } from "lucide-react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/Logo";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const areasLinks = [
  {
    title: "Aposentados e Pensionistas",
    image:
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a38acc21c5d711b35e69b02.webp",
    link: "/areas-de-atuacao/aposentados-e-pensionistas",
    description:
      "Análise de benefícios e recálculo para aposentados e pensionistas da SPPREV.",
  },
  {
    title: "Policiais",
    image:
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3c7c43ae7d47683956028e.webp",
    link: "/areas-de-atuacao/policiais",
    description:
      "Direitos e recálculos para Policiais Civis e Militares do Estado de São Paulo.",
  },
  {
    title: "Professores",
    image:
      "https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/69d807db9c84d38e9e84df33.jpg",
    link: "/areas-de-atuacao/professores-quinquenios",
    description:
      "Recálculo de quinquênios, sexta-parte e piso salarial para professores.",
  },
  {
    title: "Servidores da Saúde",
    image:
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3bf75b2ad9e46463d98d19.webp",
    link: "/areas-de-atuacao/servidores-da-saude",
    description:
      "Revisão de adicionais e direitos específicos para profissionais da saúde.",
  },
  {
    title: "Imposto de Renda",
    image:
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3c7c746a414441907d8fc9.webp",
    link: "/areas-de-atuacao/imposto-de-renda",
    description:
      "Restituição de retenções indevidas no Imposto de Renda de servidores.",
  },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const isHomePage = location.pathname === "/";

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    const handleOpen = () => setIsPopupOpen(true);
    window.addEventListener("open-home-popup", handleOpen);
    return () => window.removeEventListener("open-home-popup", handleOpen);
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://api.aquinoagency.com/js/form_embed.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleContactClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent("open-home-popup"));
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Início", href: "/" },
    { name: "Áreas de Atuação", href: "/areas-de-atuacao" },
    { name: "Sobre", href: "/sobre" },
    { name: "Avaliações", href: "/avaliacoes" },
    { name: "Blog", href: "/blog" },
  ];

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        isScrolled
          ? "bg-background/95 backdrop-blur-md border-b border-border shadow-sm"
          : !isHomePage
            ? "bg-primary shadow-md"
            : "bg-transparent border-transparent",
      )}
    >
      <div className="container mx-auto px-4 flex items-center justify-between h-20 md:h-28 transition-all duration-300 md:pb-[15px] lg:pb-0">
        <a
          href="/"
          className="flex items-center gap-2"
          aria-label="Página Inicial da Pinheiro Advocacia"
        >
          <Logo
            variant={!isScrolled ? "light" : "dark"}
            className="w-auto h-14 sm:h-16 md:h-24 transition-all duration-300"
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => {
            if (link.name === "Áreas de Atuação") {
              return (
                <NavigationMenu key={link.name} className="z-50">
                  <NavigationMenuList>
                    <NavigationMenuItem>
                      <NavigationMenuTrigger
                        className={cn(
                          "bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent data-[active]:bg-transparent",
                          "relative text-sm font-semibold transition-colors uppercase tracking-wider px-0 py-0 h-auto",
                          "after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100",
                          isScrolled
                            ? "text-primary hover:text-primary after:bg-primary"
                            : "text-white hover:text-white font-bold [text-shadow:_0_2px_10px_rgba(0,0,0,0.8),_0_1px_3px_rgba(0,0,0,0.8)] after:bg-white",
                        )}
                      >
                        {link.name}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent className="md:w-full">
                        <div className="w-full bg-white">
                          <div className="container mx-auto max-w-7xl px-4 py-8">
                            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                              {areasLinks.map((area) => (
                                <li key={area.title}>
                                  <NavigationMenuLink asChild>
                                    <a
                                      href={area.link}
                                      className="block select-none rounded-xl p-4 leading-none no-underline outline-none transition-all hover:bg-accent/5 focus:bg-accent/5 group h-full border border-transparent hover:border-accent/20"
                                    >
                                      <div className="flex flex-col gap-4 h-full">
                                        <div className="w-full aspect-video rounded-lg overflow-hidden flex-shrink-0 border border-border shadow-sm">
                                          <img
                                            src={area.image}
                                            alt={area.title}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                          />
                                        </div>
                                        <div className="flex flex-col flex-1">
                                          <div className="text-base font-bold leading-tight text-primary mb-2 group-hover:text-accent transition-colors">
                                            {area.title}
                                          </div>
                                          <p className="line-clamp-3 text-sm leading-snug text-muted-foreground font-normal normal-case tracking-normal">
                                            {area.description}
                                          </p>
                                        </div>
                                      </div>
                                    </a>
                                  </NavigationMenuLink>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  </NavigationMenuList>
                </NavigationMenu>
              );
            }

            return (
              <a
                key={link.name}
                href={link.href}
                className={cn(
                  "relative text-sm font-semibold transition-colors uppercase tracking-wider",
                  "after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100",
                  isScrolled
                    ? "text-primary hover:text-primary after:bg-primary"
                    : "text-white hover:text-white font-bold [text-shadow:_0_2px_10px_rgba(0,0,0,0.8),_0_1px_3px_rgba(0,0,0,0.8)] after:bg-white",
                )}
              >
                {link.name}
              </a>
            );
          })}
          <Button
            className="font-bold rounded-full uppercase tracking-tighter shadow-lg bg-[#075E54] hover:bg-[#043b35] text-white border-2 border-white h-auto py-[13px] px-6"
            asChild
          >
            <a
              href="#contato"
              onClick={handleContactClick}
              className="flex items-center justify-center gap-2 whitespace-nowrap text-sm"
            >
              <WhatsAppIcon className="w-5 h-5" />
              Entrar em contato
            </a>
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className={cn(
            "lg:hidden p-2 transition-colors ml-auto",
            isScrolled
              ? "text-primary"
              : "text-white [filter:drop-shadow(0_2px_4px_rgba(0,0,0,0.8))]",
          )}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
        >
          {isMenuOpen ? <X size={32} /> : <Menu size={32} />}
        </button>
      </div>

      {/* Mobile Nav */}
      {isMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-background border-b border-border p-6 flex flex-col gap-4 animate-in fade-in slide-in-from-top-4 shadow-xl max-h-[80vh] overflow-y-auto">
          {navLinks.map((link) => {
            if (link.name === "Áreas de Atuação") {
              return (
                <div
                  key={link.name}
                  className="flex flex-col border-b border-border/50 py-2"
                >
                  <span className="text-lg font-semibold text-primary mb-2">
                    {link.name}
                  </span>
                  <div className="flex flex-col gap-2 pl-4 border-l-2 border-accent/30 ml-2">
                    {areasLinks.map((area) => (
                      <a
                        key={area.title}
                        href={area.link}
                        className="text-base text-muted-foreground hover:text-accent transition-colors py-1"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {area.title}
                      </a>
                    ))}
                  </div>
                </div>
              );
            }
            return (
              <a
                key={link.name}
                href={link.href}
                className="text-lg font-semibold text-primary py-2 border-b border-border/50 transition-all duration-300 hover:text-accent hover:pl-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {link.name}
              </a>
            );
          })}
          <Button
            className="font-bold w-full py-6 mt-2 bg-[#075E54] hover:bg-[#043b35] text-white border-2 border-white uppercase"
            asChild
          >
            <a
              href="#contato"
              onClick={handleContactClick}
              className="flex items-center justify-center gap-2"
            >
              <WhatsAppIcon className="w-5 h-5" />
              Entrar em contato
            </a>
          </Button>
        </div>
      )}

      <Dialog open={isPopupOpen} onOpenChange={setIsPopupOpen}>
        <DialogContent className="max-w-[500px] w-[95vw] p-0 overflow-hidden bg-white border-none shadow-xl rounded-2xl">
          <div className="max-h-[85dvh] overflow-y-auto overflow-x-hidden pb-0 relative">
            <div className="p-6 md:p-8 pb-2 text-center space-y-2 flex flex-col items-center w-full max-w-full">
              <Logo
                variant="dark"
                className="h-auto w-[280px] md:w-[320px] max-w-full mx-auto mb-2"
              />
              <h2 className="font-bold font-serif text-primary pt-4 pb-0 mb-0 text-2xl">
                Fale com um Especialista
              </h2>
              <p className="text-muted-foreground mt-2 text-lg">
                Preencha seus dados abaixo e você será redirecionado ao nosso
                WhatsApp para que possamos analisar seu caso.
              </p>
            </div>
            <div className="w-full min-h-[550px]">
              <iframe
                src="https://api.aquinoagency.com/widget/form/cRIeYi1QkwIH0RjabWL9"
                style={{
                  width: "100%",
                  height: "520px",
                  border: "none",
                  borderRadius: "4px",
                }}
                id="inline-cRIeYi1QkwIH0RjabWL9"
                data-layout="{'id':'INLINE'}"
                data-trigger-type="alwaysShow"
                data-trigger-value=""
                data-activation-type="alwaysActivated"
                data-activation-value=""
                data-deactivation-type="neverDeactivate"
                data-deactivation-value=""
                data-form-name="Novos Leads no Site"
                data-height="520"
                data-layout-iframe-id="inline-cRIeYi1QkwIH0RjabWL9"
                data-form-id="cRIeYi1QkwIH0RjabWL9"
                title="Novos Leads no Site"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default Header;
